import BellIcon from '@assets/icons/bell.svg';
import ChevronRight from '@assets/icons/chevron-right.svg';
import EmailIcon from '@assets/icons/email.svg';
import i18n from '@/utils/i18n';
import PhoneIcon from '@assets/icons/phone.svg';
import React, { useCallback, useMemo } from 'react';
import { Avatar } from '@/components/avatar';
import { CircledIcon } from '@/components/circled-icon';
import { createStyles, useTheme } from '@/contexts/theme-context';
import {
  FlatListProps,
  Linking,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { FONTS, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { formatPhoneNumber, isNotMaliPhoneNumber } from '@/utils/phone-number';
import { List } from '@/components/list';
import { ListItemSeparator, ListOption } from '@/components/list-item';
import { RestaurantAccountListItemText } from '@/components/restaurant/restaurant-account-list-item-text';
import { TrackingProperties } from '@/utils/analytics/base-reporter';
import { useToasts } from '@/contexts/toast-context';

enum AccountListOptionType {
  Email = 'EMAIL',
  Name = 'NAME',
  Notifications = 'NOTIFICATIONS',
  PhoneNumber = 'PHONE_NUMBER',
}

interface AccountListOptionMetadata {
  onPress?: () => void;
  type: AccountListOptionType;
}

type AccountListOption = ListOption<AccountListOptionMetadata>;

const ListLeftComponent = ({ metadata, title, value }: AccountListOption) => {
  const [styles, theme] = useStyles();

  switch (metadata?.type) {
    case AccountListOptionType.Email:
      return (
        <CircledIcon
          fill={null}
          Icon={EmailIcon}
          stroke={theme.colors.white}
          style={styles.contactInfoIcon}
        />
      );
    case AccountListOptionType.Name:
      return <Avatar name={title} uuid={value as string} />;
    case AccountListOptionType.PhoneNumber:
      return (
        <CircledIcon
          fill={null}
          Icon={PhoneIcon}
          stroke={theme.colors.white}
          style={styles.contactInfoIcon}
        />
      );
    case AccountListOptionType.Notifications:
      return (
        <CircledIcon
          fill={null}
          Icon={BellIcon}
          stroke={theme.colors.white}
          style={styles.contactInfoIcon}
        />
      );
  }

  return null;
};

const ListRightComponent = ({ metadata }: AccountListOption) => {
  const theme = useTheme();

  if (metadata?.type === AccountListOptionType.Notifications) {
    return <ChevronRight fill={theme.colors.primaryOverlay} height={12} />;
  }

  return null;
};

interface Props {
  contactEmail?: string | null;
  contactPhoneNumber: string;
  containerStyle?: StyleProp<ViewStyle>;
  employeeName: string;
  employeeUUID: string;
  ListFooterComponent?: FlatListProps<AccountListOption>['ListFooterComponent'];
  loginEmail?: string | null;
  onAvatarPress?: () => void;
  onPressNotifications?: () => void;
}

export const ACCOUNT_SCREEN_WIDTH = 570;

export const RestaurantAccountComponent: React.FC<Props> = ({
  contactEmail,
  contactPhoneNumber,
  containerStyle,
  employeeName,
  employeeUUID,
  ListFooterComponent,
  loginEmail,
  onAvatarPress,
  onPressNotifications,
}) => {
  const { showErrorToast } = useToasts();
  const [styles] = useStyles();

  const linkTo = useCallback(
    async (linkUrl: string) => {
      try {
        await Linking.openURL(linkUrl);
      } catch (e) {
        showErrorToast();
      }
    },
    [showErrorToast],
  );

  const listData = useMemo<AccountListOption[]>(() => {
    const items: AccountListOption[] = [
      {
        metadata: {
          type: AccountListOptionType.Name,
        },
        title: employeeName,
        value: employeeUUID,
      },
    ];

    if (contactPhoneNumber && isNotMaliPhoneNumber(contactPhoneNumber)) {
      items.push({
        metadata: {
          onPress: async () => await linkTo(`tel:${contactPhoneNumber}`),
          type: AccountListOptionType.PhoneNumber,
        },
        title: formatPhoneNumber(contactPhoneNumber),
        value: contactPhoneNumber,
      });
    }

    if (contactEmail || loginEmail) {
      items.push({
        metadata: {
          onPress: async () => await linkTo(`mailto:${contactEmail}`),
          type: AccountListOptionType.Email,
        },
        title: contactEmail ?? loginEmail ?? '',
        value: contactEmail ?? loginEmail ?? '',
      });
    }

    if (typeof onPressNotifications === 'function') {
      items.push({
        metadata: {
          onPress: onPressNotifications,
          type: AccountListOptionType.Notifications,
        },
        title: i18n.t('notifications'),
        value: 'Notifications',
      });
    }

    return items;
  }, [
    contactEmail,
    contactPhoneNumber,
    employeeName,
    employeeUUID,
    linkTo,
    loginEmail,
    onPressNotifications,
  ]);

  return (
    <List<AccountListOption>
      contentContainerStyle={[styles.container, containerStyle]}
      data={listData}
      ItemSeparatorComponent={() => (
        <ListItemSeparator style={styles.listItemSeparator} />
      )}
      ListFooterComponent={ListFooterComponent}
      ListHeaderComponent={
        <TouchableOpacity
          disabled={!onAvatarPress}
          onPress={onAvatarPress}
          style={styles.avatarContainer}
        >
          <Avatar name={employeeName} size="xlarge" uuid={employeeUUID} />
        </TouchableOpacity>
      }
      listItemStyle={styles.listItemBase}
      renderLeftComponent={ListLeftComponent}
      renderMainContent={(listItem: AccountListOption) => {
        const trackingProperties: TrackingProperties = {
          employeeUUID,
          type: listItem.metadata?.type,
        };

        if (
          (loginEmail || contactEmail) &&
          listItem.metadata?.type === AccountListOptionType.Email
        ) {
          return (
            <View style={styles.listItemContent}>
              {contactEmail ? (
                <View collapsable={false}>
                  <Text style={[TYPOGRAPHY.xsmall, styles.loginEmailLabel]}>
                    {i18n.t('contact')}
                  </Text>
                  <RestaurantAccountListItemText
                    onPress={listItem.metadata!.onPress}
                    style={styles.contactEmail}
                    trackingProperties={{
                      ...trackingProperties,
                      subtype: 'contact',
                    }}
                  >
                    {listItem.title}
                  </RestaurantAccountListItemText>
                </View>
              ) : null}
              {loginEmail ? (
                <View collapsable={false}>
                  <Text style={[TYPOGRAPHY.xsmall, styles.loginEmailLabel]}>
                    {i18n.t('login')}
                  </Text>
                  <RestaurantAccountListItemText
                    onPress={async () => await linkTo(`mailto:${loginEmail}`)}
                    trackingProperties={{
                      ...trackingProperties,
                      subtype: 'login',
                    }}
                  >
                    {loginEmail}
                  </RestaurantAccountListItemText>
                </View>
              ) : null}
            </View>
          );
        }

        return (
          <View style={styles.listItemContent}>
            <RestaurantAccountListItemText
              onPress={listItem.metadata!.onPress}
              trackingProperties={trackingProperties}
            >
              {listItem.title}
            </RestaurantAccountListItemText>
          </View>
        );
      }}
      renderRightComponent={ListRightComponent}
    />
  );
};

const useStyles = createStyles((theme) => ({
  avatarContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  contactEmail: {
    marginBottom: 16,
  },
  contactInfoIcon: {
    backgroundColor: theme.colors.primary,
    borderWidth: 0,
  },
  container: {
    marginTop: SCREEN_PADDING,
  },
  listItemBase: {
    paddingHorizontal: 0,
  },
  listItemContent: {
    flex: 1,
  },
  listItemSeparator: {
    marginHorizontal: 0,
  },
  loginEmailLabel: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
    marginBottom: 2,
    textTransform: 'uppercase',
  },
}));
